import { useSelector } from 'react-redux';
import { useAddSubscriptionMutation } from '../../../../store/services/api';
import { selectIsDev } from '../../../../store/slices/user';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { colors } from '../../../../colors';
import InputField from '../../../../components/Form/InputField';
import { formatNumber } from '../../../../helpers/NumberFormatter';
import AssetIcon from '../../../../components/AssetIcon';
import { Validations } from '../../../../components/Form';
import LoadingIcons from 'react-loading-icons';
import CalculationSummary from '../CalculationSummary';
import { GetTopAssetsByVolumeInfo } from '../../../../types/Bot';
import ExpandableModal, {
  ExpandableModalChildProps,
} from '../../../../components/ExpandableModal';
import { BotItemContext, useBotItemContext } from './context';
import { Subscription } from '../../../../types/subscription';
import useWidthBreakpoint from '../../../../hooks/useWidthBreakpoint';
import DropdownComponent from '../../../../components/Form/Dropdown';
import Tag from '../../Tag';

interface BotItemProps {
  userId: number | null;
  baseAsset: string;
  options: GetTopAssetsByVolumeInfo[];
  balances: Record<string, Record<string, number>> | undefined;
  activeBotForm: string | undefined;
  setActiveBotForm: React.Dispatch<React.SetStateAction<string | undefined>>;
  credits: number;
  subscriptions: Subscription[];
  refresh: () => void;
  parentRef: React.RefObject<HTMLDivElement>;
}

const BotItem = ({
  userId,
  baseAsset,
  options,
  balances,
  activeBotForm,
  setActiveBotForm,
  credits,
  subscriptions,
  refresh,
  parentRef,
}: BotItemProps) => {
  return (
    <BotItemContext
      baseAsset={baseAsset}
      options={options}
      balances={balances}
      credits={credits}
      subscriptions={subscriptions}
      activeBotForm={activeBotForm}
      setActiveBotForm={setActiveBotForm}
    >
      <ExpandableModal key={baseAsset} parentRef={parentRef} supportScrolling>
        <BotItemContent
          baseAsset={baseAsset}
          userId={userId}
          refresh={refresh}
        />
      </ExpandableModal>
    </BotItemContext>
  );
};

type BotItemContentProps = ExpandableModalChildProps & {
  baseAsset: string;
  userId: number | null;
  refresh: () => void;
};

const BotItemContent = forwardRef<HTMLDivElement, BotItemContentProps>(
  (props, childRef) => {
    const { baseAsset, userId, refresh } = props;
    const { handleModalOpen, handleModalClose, style, isModalOpen } = props;

    const { isMatch } = useWidthBreakpoint();
    const formScrollableDivRef = useRef<HTMLDivElement>(null);

    const {
      possibleQuoteAssets,
      selectedQuoteAsset,
      handleSelectedQuoteAsset,
      possibleExchanges,
      selectedExchange,
      setSelectedExchange,
      tradedVolumePast24h,
      availableBaseBalance,
      availableQuoteBalance,
      isActive,
      canSubscribe,
      activeBotForm,
      setActiveBotForm,
      activeAsset,
      setActiveAsset,
    } = useBotItemContext();

    const handleOpenFormPress = (value: boolean) => {
      setActiveBotForm(value ? baseAsset : undefined);

      if (value === true) {
        handleModalOpen?.();
      } else {
        handleModalClose?.();
      }
    };

    useEffect(() => {
      if (!isModalOpen) {
        return;
      }

      setTimeout(() => {
        formScrollableDivRef.current?.scrollTo({
          top: formScrollableDivRef.current.getBoundingClientRect().height,
          behavior: 'smooth',
        });
      }, 500);
    }, [isModalOpen]);

    return (
      <div
        ref={childRef}
        style={{
          border: '1px solid rgba(50, 50, 50, 1)',
          borderRadius: 8,
          padding: 16,
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          backgroundColor: 'rgba(30, 30, 30, 1)',
          boxShadow: '4px 0px 5px rgba(255, 255, 255, 0.2)',
          height: 'fit-content',
          transition: 'opacity 0.5s ease',
          opacity: !activeBotForm ? 1 : isModalOpen ? 1 : 0,
          ...style,
          overflowX: isModalOpen && !isMatch('md') ? 'scroll' : ' none',
        }}
      >
        <div
          style={{
            flex: 1,
            flexGrow: isModalOpen ? 0 : 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRight: isModalOpen ? '1px solid #303030' : undefined,
            paddingRight: isModalOpen ? 20 : 0,
          }}
        >
          <div
            ref={formScrollableDivRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              marginRight: -20,
              paddingRight: 20,
              position: 'relative',
            }}
            className="no-scrollbar"
          >
            <AssetIcon baseAsset={baseAsset} height={60} width={60} />

            <div style={{ marginTop: 16 }}>
              <p style={{ color: colors.gray, fontSize: 14, margin: 0 }}>
                Base asset:
              </p>
              <p style={{ color: colors.white, margin: 0, marginTop: -5 }}>
                {baseAsset}
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                marginTop: 8,
                flexDirection: 'column',
              }}
            >
              <p
                style={{
                  color: colors.gray,
                  fontSize: 14,
                  margin: 0,
                  marginBottom: 4,
                }}
              >
                Quote asset:
              </p>
              <div
                style={{
                  display: 'grid',
                  gap: 8,
                  gridTemplateColumns: 'min-content min-content',
                }}
              >
                {possibleQuoteAssets.map((quoteAsset) => (
                  <Tag
                    key={quoteAsset}
                    value={quoteAsset}
                    handlePress={() => handleSelectedQuoteAsset(quoteAsset)}
                    isSelected={selectedQuoteAsset === quoteAsset}
                  />
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                marginTop: 8,
                flexDirection: 'column',
              }}
            >
              <p
                style={{
                  color: colors.gray,
                  fontSize: 14,
                  margin: 0,
                  marginBottom: 4,
                }}
              >
                Exchange:
              </p>
              <div
                style={{
                  display: 'grid',
                  gap: 8,
                  gridTemplateColumns: 'min-content min-content',
                }}
              >
                {possibleExchanges.map((exchange) => (
                  <Tag
                    key={exchange}
                    value={exchange}
                    handlePress={() => setSelectedExchange(exchange)}
                    isSelected={selectedExchange === exchange}
                  />
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                marginTop: 8,
                flexDirection: 'column',
              }}
            >
              <p
                style={{
                  color: colors.gray,
                  fontSize: 14,
                  margin: 0,
                  marginBottom: 4,
                  textWrap: 'nowrap',
                }}
              >
                Traded volume past 24h:
              </p>
              <p
                style={{
                  color: colors.white,
                  fontSize: 16,
                  margin: 0,
                  marginTop: -5,
                }}
              >
                {formatNumber(tradedVolumePast24h ?? 0, 0)}
              </p>
            </div>

            <SubscribeForm
              baseAsset={baseAsset}
              quoteAsset={selectedQuoteAsset}
              isFormOpen={!!isModalOpen}
              availableBaseBalance={availableBaseBalance}
              availableQuoteBalance={availableQuoteBalance}
              isActive={isActive}
              canSubscribe={canSubscribe}
              activeAsset={activeAsset}
            />
          </div>
          <ActionButtons
            userId={userId}
            baseAsset={baseAsset}
            refresh={refresh}
            isFormOpen={!!isModalOpen}
            setIsFormOpen={handleOpenFormPress}
          />
        </div>
        {isModalOpen && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              justifyContent: 'center',
              alignItems: isActive ? undefined : 'center',
            }}
          >
            <CalculationSummary
              baseAsset={baseAsset}
              setActiveAsset={setActiveAsset}
            />
          </div>
        )}
      </div>
    );
  },
);

BotItemContent.displayName = 'BotItemContent';

interface ActionButtonsProps {
  userId: number | null;
  baseAsset: string;
  isFormOpen: boolean;
  setIsFormOpen: (value: boolean) => void;
  refresh: () => void;
}

const ActionButtons = ({
  userId,
  baseAsset,
  isFormOpen,
  setIsFormOpen,
  refresh,
}: ActionButtonsProps) => {
  const isDev = useSelector(selectIsDev);
  const {
    canSubscribe,
    isActive,
    selectedQuoteAsset,
    selectedExchange,
    availableQuoteBalance,
    availableBaseBalance,
    stableBalance,
    setStableBalance,
    assetBalance,
    setAssetBalance,
    selectedBasket,
    success,
    setSuccess,
    setError,
  } = useBotItemContext();

  const [addSubscription] = useAddSubscriptionMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!userId) {
      return;
    }

    const postData = {
      userId,
      baseAsset,
      quoteAsset: selectedQuoteAsset,
      exchange: selectedExchange,
      stableBalance: parseFloat(stableBalance),
      assetBalance: parseFloat(assetBalance),
      basket: selectedBasket,
      requestBot: !isActive,
    };

    setIsLoading(true);
    const result = await addSubscription(postData);

    if (result.data?.success) {
      refresh();
    }

    setSuccess(result.data?.success);
    setError(result.data?.error);
    setIsLoading(false);

    return null;
  };

  if (!isFormOpen) {
    const isButtonDisabled = !isDev && !canSubscribe;
    const buttonText = isActive ? 'Subscribe' : 'Request and Subscribe';

    return (
      <div
        style={{
          display: 'flex',
          marginTop: 16,
          justifyContent: 'end',
          marginLeft: 8,
        }}
      >
        <button
          onClick={() => setIsFormOpen(true)}
          style={{
            backgroundColor: !isButtonDisabled
              ? isActive
                ? colors.green
                : colors.orange
              : colors.gray,
            border: `1px solid ${
              !isButtonDisabled
                ? isActive
                  ? colors.green
                  : colors.orange
                : colors.gray
            }`,
            borderRadius: 4,
            cursor: !isButtonDisabled ? 'pointer' : 'default',
            padding: 6,
            paddingLeft: 12,
            paddingRight: 12,
            color: colors.white,
            fontWeight: 'bold',
            textWrap: 'nowrap',
          }}
          disabled={isButtonDisabled}
        >
          {buttonText}
        </button>
      </div>
    );
  }

  const isStableBalanceWrong =
    parseFloat(stableBalance) < 0 ||
    parseFloat(stableBalance) > availableQuoteBalance;
  const isAssetBalanceWrong =
    parseFloat(assetBalance) < 0 ||
    parseFloat(assetBalance) > availableBaseBalance;

  const buttonText = isActive ? 'Subscribe' : 'Request and Subscribe';
  const disableButton =
    !isDev &&
    (!userId ||
      !!success ||
      (parseFloat(stableBalance) === 0 && parseFloat(assetBalance) === 0) ||
      isStableBalanceWrong ||
      isAssetBalanceWrong ||
      selectedBasket.length === 0);

  const handleCancel = () => {
    setIsFormOpen(false);
    setStableBalance('0');
    setAssetBalance('0');
  };

  return (
    <div
      style={{
        display: 'flex',
        marginTop: 16,
        justifyContent: 'end',
        marginLeft: 8,
        gap: 16,
      }}
    >
      <button
        onClick={handleConfirm}
        style={{
          backgroundColor: disableButton
            ? colors.gray
            : isActive
            ? colors.green
            : colors.orange,
          border: `1px solid ${
            disableButton
              ? colors.gray
              : isActive
              ? colors.green
              : colors.orange
          }`,
          borderRadius: 4,
          cursor: disableButton ? 'default' : 'pointer',
          padding: 6,
          paddingLeft: 12,
          paddingRight: 12,
          color: colors.white,
          fontWeight: 'bold',
          textWrap: 'nowrap',
        }}
        disabled={disableButton}
      >
        {isLoading ? <LoadingIcons.Grid width={15} height={15} /> : buttonText}
      </button>
      <button
        onClick={handleCancel}
        style={{
          backgroundColor: colors.red,
          border: `1px solid ${colors.red}`,
          borderRadius: 4,
          cursor: 'pointer',
          padding: 6,
          paddingLeft: 12,
          paddingRight: 12,
          color: colors.white,
          fontWeight: 'bold',
          textWrap: 'nowrap',
        }}
      >
        Cancel
      </button>
    </div>
  );
};

const SubscribeForm = ({
  baseAsset,
  quoteAsset,
  isFormOpen,
  availableBaseBalance,
  availableQuoteBalance,
  isActive,
  activeAsset,
}: {
  baseAsset: string;
  quoteAsset: string;
  isFormOpen: boolean;
  availableBaseBalance: number;
  availableQuoteBalance: number;
  isActive: boolean | undefined;
  canSubscribe: boolean;
  activeAsset: string | boolean | undefined;
}) => {
  const {
    stableBalance,
    setStableBalance,
    assetBalance,
    setAssetBalance,
    selectedBasket,
    setSelectedBasket,
    basketOptions,
    shouldCreateNewBasket,
    success,
    error,
  } = useBotItemContext();

  const isStableBalanceWrong =
    parseFloat(stableBalance) < 0 ||
    parseFloat(stableBalance) > availableQuoteBalance;
  const isAssetBalanceWrong =
    parseFloat(assetBalance) < 0 ||
    parseFloat(assetBalance) > availableBaseBalance;

  const availableQuoteAssetColor = activeAsset
    ? activeAsset === baseAsset
      ? colors.gray
      : colors.green
    : colors.gray;
  const availableBaseAssetColor = activeAsset
    ? activeAsset === baseAsset
      ? colors.green
      : colors.gray
    : colors.gray;

  if (!isFormOpen) {
    return null;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            marginTop: 8,
            flexDirection: 'column',
          }}
        >
          <p
            style={{
              color: availableQuoteAssetColor,
              fontSize: 14,
              margin: 0,
            }}
          >
            Available {quoteAsset}
          </p>
          <p
            style={{
              color: availableQuoteBalance < 0 ? colors.red : colors.white,
              fontSize: 16,
              margin: 0,
              marginTop: -5,
            }}
          >
            {formatNumber(Math.max(availableQuoteBalance ?? 0, 0), 2)}
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            marginTop: 8,
            flexDirection: 'column',
            textAlign: 'end',
          }}
        >
          <p
            style={{
              color: availableBaseAssetColor,
              fontSize: 14,
              margin: 0,
            }}
          >
            Available {baseAsset}
          </p>
          <p
            style={{
              color: availableBaseBalance < 0 ? colors.red : colors.white,
              fontSize: 16,
              margin: 0,
              marginTop: -5,
            }}
          >
            {formatNumber(Math.max(availableBaseBalance ?? 0, 0), 8)}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', columnGap: 16, marginTop: 8 }}>
        <InputField
          config={{
            label: `Stable Balance (${quoteAsset})`,
            value: stableBalance,
            handleValueChange: setStableBalance,
            shouldFit: true,
            error: isStableBalanceWrong
              ? Validations.MaximumExceeded
              : undefined,
            isDisabled: success,
          }}
          width={120}
        />
        <InputField
          config={{
            label: `Asset Balance (${baseAsset})`,
            value: assetBalance,
            handleValueChange: setAssetBalance,
            shouldFit: true,
            error: isAssetBalanceWrong
              ? Validations.MaximumExceeded
              : undefined,
            isDisabled: success,
          }}
          width={120}
        />
      </div>

      <div style={{ display: 'flex', marginTop: 8 }}>
        {shouldCreateNewBasket ? (
          <InputField
            config={{
              label: 'Basket',
              value: selectedBasket,
              handleValueChange: setSelectedBasket,
              shouldFit: true,
              isDisabled: success,
            }}
            width={'100%'}
          />
        ) : (
          <DropdownComponent
            config={{
              label: 'Basket',
              value: selectedBasket,
              handleValueChange: setSelectedBasket,
              options: [
                ...basketOptions.filter((b) => b !== selectedBasket),
                'Create new...',
              ],
              fullWidth: true,
            }}
          />
        )}
      </div>

      <div style={{ marginTop: 8, marginBottom: -4 }}>
        {!success && !error && (
          <p
            style={{
              margin: 0,
              color: colors.orange,
              fontSize: 14,
              fontWeight: 'bold',
            }}
          >
            This action will require {isActive ? 1 : 2} credits.
          </p>
        )}

        {success && (
          <p
            style={{
              margin: 0,
              color: colors.green,
              fontSize: 14,
              fontWeight: 'bold',
            }}
          >
            You have successfully subscribed to this bot!
          </p>
        )}
        {error && (
          <p
            style={{
              margin: 0,
              color: colors.red,
              fontSize: 14,
              fontWeight: 'bold',
            }}
          >
            Something went wrong subscribing to this bot!
            <br />
            {error}
          </p>
        )}
      </div>

      {activeAsset !== false && (
        <span
          style={{
            margin: 0,
            color: colors.orange,
            fontSize: 14,
            fontWeight: 'bold',
          }}
        >
          Current active asset is{' '}
          {baseAsset === activeAsset ? baseAsset : quoteAsset || 'loading...'}.
        </span>
      )}
    </>
  );
};

export default BotItem;
